/**
 * useScrollAutoFill — quando una lista paginata sta tutta dentro l'altezza
 * del suo container, lo scroll event non scatta mai e `loadMore` resta
 * inattivo. Questo hook aggancia un ref al container e, dopo ogni render
 * che cambia `deps`, controlla se il contenuto copre il container: se
 * `scrollHeight <= clientHeight` (STRETTO, senza threshold), triggera
 * `loadMore` finche' appare uno scroll utile. Appena anche 1px di scroll
 * compare, si ferma e lascia l'utente guidare con `onScroll` sul
 * container.
 *
 * Caveat: il check stretto e' deliberato. Una versione con threshold
 * (es. `scrollHeight < clientHeight + 50`) puo' loopare all'infinito
 * se il contenuto cresce a piccoli step e non supera mai la soglia.
 *
 * Uso tipico, accanto a `useServerPagedSearch`:
 * ```tsx
 * const listRef = useRef<HTMLDivElement | null>(null);
 * const { items, hasMore, loading, loadMore } = useServerPagedSearch(...);
 * useScrollAutoFill(listRef, { hasMore, loading, loadMore, deps: [items] });
 *
 * <div ref={listRef} onScroll={handleScroll}>...</div>
 * ```
 */

import { useEffect, type RefObject } from 'react';

interface UseScrollAutoFillOptions {
  hasMore: boolean;
  loading: boolean;
  loadMore: () => void;
  /** Valori che, cambiando, ri-triggano il check (tipicamente `items`). */
  deps: ReadonlyArray<unknown>;
}

export function useScrollAutoFill(
  ref: RefObject<HTMLElement | null>,
  opts: UseScrollAutoFillOptions,
): void {
  const { hasMore, loading, loadMore, deps } = opts;
  // JSON.stringify come proxy stabile dell'array deps — lo stesso pattern
  // usato da useServerPagedSearch per `resetOn`.
  const depsKey = JSON.stringify(deps);
  useEffect(() => {
    if (loading || !hasMore) return;
    const el = ref.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight) loadMore();
    // ref e' stabile, deps tracciate via depsKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadMore, depsKey]);
}
